import { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, BookOpen, HelpCircle, ChevronRight, Crown, X } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { useGuides, useGuideSearch } from '../hooks/useGuides';
import { useSubscription } from '@/features/subscription/hooks/useSubscription';
import { GuideSuggestionModal } from '../components/GuideSuggestionModal';
import type { GuideSearchResult } from '../types';

export function GuidesPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('guides');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const [searchPosition, setSearchPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  const { data: guides, isLoading } = useGuides();
  const { data: searchResults, isLoading: isSearching } = useGuideSearch(searchQuery);
  const { isPremium } = useSubscription();

  // Track search container position for portal rendering
  useLayoutEffect(() => {
    if (placeholderRef.current && showSearchResults && searchQuery.length >= 2) {
      const rect = placeholderRef.current.getBoundingClientRect();
      setSearchPosition({ top: rect.top, left: rect.left, width: rect.width });
    }
  }, [showSearchResults, searchQuery]);

  // Update position on scroll/resize when search is active
  useEffect(() => {
    if (!showSearchResults || searchQuery.length < 2) return;

    const updatePosition = () => {
      if (placeholderRef.current) {
        const rect = placeholderRef.current.getBoundingClientRect();
        setSearchPosition({ top: rect.top, left: rect.left, width: rect.width });
      }
    };

    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showSearchResults, searchQuery]);

  // Close search results when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowSearchResults(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearchResultClick = (result: GuideSearchResult) => {
    if (result.is_premium && !isPremium) {
      navigate('/subscription');
      return;
    }

    if (result.type === 'term') {
      navigate(`/tools/guides/glossar/${result.guide_slug}?term=${result.id}`);
    } else {
      navigate(`/tools/guides/fragen/${result.guide_slug}?faq=${result.id}`);
    }
    setSearchQuery('');
    setShowSearchResults(false);
  };

  const isSearchActive = showSearchResults && searchQuery.length >= 2;

  // Search container content - reused in normal and portal rendering
  const searchContainerContent = (
    <div className="flex items-center gap-3" ref={searchRef}>
      <div className="flex-1 relative">
        <div className="bg-white rounded-[15px] flex items-center overflow-hidden">
          <input
            type="text"
            placeholder={t('searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowSearchResults(true);
            }}
            onFocus={() => setShowSearchResults(true)}
            className="flex-1 px-4 py-3 text-pp-dark placeholder-pp-dark/50 text-base focus:outline-none"
          />
          {searchQuery ? (
            <button
              onClick={() => {
                setSearchQuery('');
                setShowSearchResults(false);
              }}
              className="px-3 text-pp-dark/50 hover:text-pp-dark"
            >
              <X className="w-5 h-5" />
            </button>
          ) : (
            <div className="px-3 text-pp-dark/50">
              <Search className="w-5 h-5" />
            </div>
          )}
        </div>

        {/* Search Results Dropdown */}
        {isSearchActive && !showSuggestionModal && (
          <div className="absolute top-full left-0 right-0 mt-2 glass-effect rounded-[15px] p-3">
            {isSearching ? (
              <div className="text-center py-4 text-white/50 text-sm">
                {t('searching')}
              </div>
            ) : searchResults && searchResults.length > 0 ? (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {searchResults.map((result) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleSearchResultClick(result)}
                    className="w-full text-left p-3 hover:bg-white/10 rounded-xl transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center bg-pp-dark/25">
                        {result.type === 'term' ? (
                          <BookOpen className="w-4 h-4 text-pp-orange" />
                        ) : (
                          <HelpCircle className="w-4 h-4 text-pp-orange" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-white truncate">
                            {result.title}
                          </span>
                          {result.is_premium && !isPremium && (
                            <Crown className="w-4 h-4 text-amber-400 flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-sm text-white/50 line-clamp-1">
                          {result.preview}
                        </p>
                        <div className="flex items-center gap-1 mt-1 text-xs text-white/40">
                          {result.breadcrumbs.map((crumb, idx) => (
                            <span key={idx} className="flex items-center">
                              {idx > 0 && <ChevronRight className="w-3 h-3 mx-1" />}
                              {crumb}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-white/50 text-sm">
                {t('noResults')}
              </div>
            )}
          </div>
        )}
      </div>
      <button
        onClick={() => {
          setShowSearchResults(false);
          setShowSuggestionModal(true);
        }}
        className="flex items-center justify-center w-12 h-12 bg-pp-orange hover:bg-pp-orange/90 text-white rounded-[15px] transition-colors flex-shrink-0"
        title={t('suggestTooltip')}
      >
        <Plus className="w-6 h-6" />
      </button>
    </div>
  );

  return (
    <>
      {/* Search Backdrop + Container - rendered via portal for iOS z-index fix */}
      {isSearchActive && !showSuggestionModal && searchPosition && createPortal(
        <>
          <div
            className="fixed inset-0 bg-pp-dark/60 backdrop-blur-sm z-[9998]"
            onClick={() => setShowSearchResults(false)}
          />
          <div
            className="fixed z-[9999]"
            style={{
              top: searchPosition.top,
              left: searchPosition.left,
              width: searchPosition.width,
            }}
          >
            {searchContainerContent}
          </div>
        </>,
        document.body
      )}

      {/* Header */}
      <h1 className="text-white text-xl font-semibold mb-6">{t('pageTitle')}</h1>

      {/* Search with Plus Button - Placeholder when portal is active, normal when not */}
      <div
        ref={placeholderRef}
        className={`mb-6 ${isSearchActive && !showSuggestionModal ? 'invisible' : ''}`}
      >
        {searchContainerContent}
      </div>

      {/* Feature Cards */}
      <section className="space-y-3 mb-6 mt-8">
        {/* Fachwörter */}
        <button
          onClick={() => navigate('/tools/guides/glossar')}
          className="w-full glass-effect rounded-[15px] p-4 text-left hover:bg-white/10 transition-all"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-6 h-6 text-pp-orange" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-semibold">{t('sections.glossary')}</h3>
              <p className="text-white/70 text-sm">{t('sections.glossaryDescription')}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-pp-orange" />
          </div>
        </button>

        {/* Fragen zu Anträgen */}
        <button
          onClick={() => navigate('/tools/guides/fragen')}
          className="w-full glass-effect rounded-[15px] p-4 text-left hover:bg-white/10 transition-all"
        >
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
              <HelpCircle className="w-6 h-6 text-pp-orange" />
            </div>
            <div className="flex-1">
              <h3 className="text-white font-semibold">{t('sections.faq')}</h3>
              <p className="text-white/70 text-sm">{t('sections.faqDescription')}</p>
            </div>
            <ChevronRight className="w-5 h-5 text-pp-orange" />
          </div>
        </button>
      </section>

      {/* Enthaltene Anträge */}
      <section className="mt-8">
        <h2 className="text-sm font-semibold text-white/80 mb-3 px-2">{t('includedGuides')}</h2>

        {isLoading ? (
          <div className="glass-effect rounded-[15px] divide-y divide-white/10 animate-pulse">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="p-4">
                <div className="h-5 bg-white/10 rounded w-1/3 mb-2"></div>
                <div className="h-4 bg-white/10 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : (
          <div className="glass-effect rounded-[15px] divide-y divide-white/10">
            {guides?.map((guide) => (
              <div key={guide.id} className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-white font-semibold">{guide.title}</h3>
                  {guide.is_premium && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500/20 text-amber-300 text-xs font-medium rounded-full">
                      <Crown className="w-3 h-3" />
                      {t('premium')}
                    </span>
                  )}
                </div>
                {guide.short_description && (
                  <p className="text-white/70 text-sm">{guide.short_description}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Suggestion Modal */}
      <GuideSuggestionModal
        isOpen={showSuggestionModal}
        onClose={() => setShowSuggestionModal(false)}
        guides={guides || []}
      />
    </>
  );
}
